import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import {
  AiDebugService,
  AiHealthData,
  AiIndexInfoData,
  AiReindexJobData,
  RagReindexStatusData,
  ReindexHistoryEntry,
} from './ai-debug.service';
import {
  appendAiIndexActivity,
  getRecentAiIndexActivity,
} from '../_util/ai-index-activity.util';
import { AiIndexInspectorComponent } from './ai-index-inspector.component';

const ACTIVE_REINDEX_JOB_STORAGE_KEY = 'ai-debug.active-reindex-job';

@Component({
  selector: 'app-ai-debug',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, AiIndexInspectorComponent],
  templateUrl: './ai-debug.component.html',
})
export class AiDebugComponent implements OnInit, OnDestroy {
  private readonly service = inject(AiDebugService);
  private readonly router = inject(Router);
  private reindexPollSub?: Subscription;
  private reindexPollTimer?: ReturnType<typeof setTimeout>;
  private readonly reindexPollInitialMs = 1000;
  private readonly reindexPollMaxMs = 10000;
  private reindexPollDelayMs = this.reindexPollInitialMs;
  private latestStartedReindexJobId: string | null = null;
  private singleNoteVerifyTimers = new Map<string, any>();
  private readonly singleNoteVerifyIntervalMs = 5000;
  private readonly singleNoteVerifyMaxAttempts = 18;

  private readonly terminalReindexStates = new Set(['completed', 'completed_with_errors', 'failed']);
  private readonly lastLoggedReindexStatusByJob = new Map<string, string>();

  loading = false;
  diagnosticsLoading = false;
  reindexing = false;
  clearing = false;
  creatingSingleNoteIndex = false;
  deletingSingleNoteIndex = false;

  message = '';
  error = '';

  pendingSingleNoteJobs = new Map<string, any>();

  reindexLimit: number | null = null;
  indexOperationNoteId = '';
  activeReindexJobId: string | null = null;
  activeReindexJobStatus = '';
  activeReindexSummary = '';
  activeReindexLastUpdated = '';
  activeReindexStartedAt = '';
  activeReindexProgressPercent: number | null = null;
  activeReindexRequestedLimit: number | null = null;
  totalDocsInDatabase = 0;
  docsProcessed = 0;
  chunksIndexed = 0;
  lastActivityAt: string | null = null;
  isJobStalled = false;

  healthData: AiHealthData | null = null;
  indexInfo: AiIndexInfoData | null = null;
  reindexStatus: RagReindexStatusData | null = null;

  recentIndexEntries: ReindexHistoryEntry[] = [];
  activeTab: 'overview' | 'maintenance' | 'debug' = 'overview';


  ngOnInit(): void {
    this.clearPersistedReindexJob();
    this.refreshIndexHistory();
    this.refreshDiagnostics(false);
  }

  ngOnDestroy(): void {
    this.stopReindexPolling();
    this.clearSingleNoteTimers();
  }

  private clearSingleNoteTimers(): void {
    for (const timer of this.singleNoteVerifyTimers.values()) {
      clearTimeout(timer);
    }
    this.singleNoteVerifyTimers.clear();
  }

  get indexedNoteCount(): number {
    return Number(this.indexInfo?.item_count ?? 0);
  }

  get lastFullReindexTime(): string {
    const statusAny = (this.reindexStatus ?? {}) as Record<string, unknown>;
    const fromStatus = this.firstNonEmptyString([
      statusAny['finished_at'],
      statusAny['finishedAt'],
      statusAny['completed_at'],
      statusAny['completedAt'],
      statusAny['last_full_reindex_at'],
      statusAny['lastFullReindexAt'],
      statusAny['last_reindex_at'],
      statusAny['lastReindexAt'],
      statusAny['started_at'],
      statusAny['startedAt'],
    ]);
    if (fromStatus) {
      return fromStatus;
    }

    const lastBulkJob = this.recentIndexEntries.find((entry) => {
      const kind = String(entry.job_type ?? '').toLowerCase();
      return kind === 'bulk' || kind === 'full' || kind === 'full_reindex';
    });
    if (lastBulkJob?.finished_at) {
      return lastBulkJob.finished_at;
    }
    if (lastBulkJob?.started_at) {
      return lastBulkJob.started_at;
    }

    const localFull = getRecentAiIndexActivity(50).find((entry) => entry.kind === 'full_reindex');
    if (localFull?.timestamp) {
      return localFull.timestamp;
    }

    return '-';
  }

  get lastIndexingError(): string {
    const value = String(this.reindexStatus?.error ?? '').trim();
    if (value) {
      return value;
    }

    const errorEntry = this.recentIndexEntries.find(
      (entry) => entry.status === 'failed' || entry.status === 'completed_with_errors' || entry.error,
    );
    if (!errorEntry) {
      return '-';
    }

    return errorEntry.error || errorEntry.status || '-';
  }

  refreshIndexHistory(): void {
    this.service.getReindexHistory(10).subscribe({
      next: (res) => {
        const backendEntries = this.extractHistoryEntriesFromResponse(res, 50);
        const localEntries = this.buildHistoryFromLocalActivity(50);
        const localTitleByNoteId = new Map<string, string>();
        for (const entry of localEntries) {
          if (entry.note_id && entry.note_title) {
            localTitleByNoteId.set(entry.note_id, entry.note_title);
          }
        }

        const enrichedBackendEntries = backendEntries.map((entry) => {
          if (entry.note_title || !entry.note_id) {
            return entry;
          }
          const fallbackTitle = localTitleByNoteId.get(entry.note_id);
          return fallbackTitle ? { ...entry, note_title: fallbackTitle } : entry;
        });

        this.recentIndexEntries = this.mergeHistoryEntries([enrichedBackendEntries, localEntries], 10);
      },
      error: () => {
        this.recentIndexEntries = this.buildHistoryFromLocalActivity(10);
      },
    });
  }

  private extractHistoryEntriesFromResponse(payload: unknown, limit = 10): ReindexHistoryEntry[] {
    const source = (payload ?? {}) as Record<string, unknown>;
    const data = (source['data'] ?? {}) as Record<string, unknown>;

    const candidates: unknown[] = [
      data['entries'],
      data['history'],
      data['items'],
      source['entries'],
      source['history'],
      source['items'],
      data,
      source,
    ];

    let rawEntries: unknown[] = [];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        rawEntries = candidate;
        break;
      }
    }

    return rawEntries
      .map((entry) => this.normalizeHistoryEntry(entry))
      .filter((entry): entry is ReindexHistoryEntry => !!entry)
      .sort((a, b) => {
        const aTs = new Date(a.finished_at ?? a.started_at ?? a.submitted_at ?? 0).getTime();
        const bTs = new Date(b.finished_at ?? b.started_at ?? b.submitted_at ?? 0).getTime();
        return bTs - aTs;
      })
      .slice(0, limit);
  }

  private mergeHistoryEntries(groups: ReindexHistoryEntry[][], limit: number): ReindexHistoryEntry[] {
    const seen = new Set<string>();
    const merged: ReindexHistoryEntry[] = [];

    for (const group of groups) {
      for (const entry of group) {
        const key = this.historyEntryKey(entry);
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        merged.push(entry);
      }
    }

    return merged
      .sort((a, b) => {
        const aTs = new Date(a.finished_at ?? a.started_at ?? a.submitted_at ?? 0).getTime();
        const bTs = new Date(b.finished_at ?? b.started_at ?? b.submitted_at ?? 0).getTime();
        return bTs - aTs;
      })
      .slice(0, limit);
  }

  private historyEntryKey(entry: ReindexHistoryEntry): string {
    return [
      entry.job_id ?? '',
      entry.job_type ?? '',
      entry.note_id ?? '',
      entry.note_title ?? '',
      entry.status ?? '',
      entry.submitted_at ?? '',
      entry.started_at ?? '',
      entry.finished_at ?? '',
      entry.error ?? '',
      entry.processed_count ?? '',
      entry.indexed_count ?? '',
      entry.skipped_count ?? '',
      entry.chunks_indexed ?? '',
    ].join('|');
  }

  private normalizeHistoryEntry(value: unknown): ReindexHistoryEntry | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const source = value as Record<string, unknown>;
    const nestedResult =
      source['result'] && typeof source['result'] === 'object'
        ? (source['result'] as Record<string, unknown>)
        : {};

    const merged: Record<string, unknown> = {
      ...source,
      ...nestedResult,
    };

    const jobTypeRaw = this.firstNonEmptyString([
      merged['job_type'],
      merged['type'],
      merged['kind'],
      merged['operation_type'],
    ]);
    const sourceLabel = this.firstNonEmptyString([merged['source'], merged['operation'], merged['operation_type']]);
    const status = this.firstNonEmptyString([
      merged['status'],
      merged['job_status'],
      merged['state'],
      merged['result_status'],
    ]);

    const submittedAt = this.firstNonEmptyString([
      merged['submitted_at'],
      merged['queued_at'],
      merged['created_at'],
      merged['createdAt'],
      merged['timestamp'],
    ]);
    const startedAt = this.firstNonEmptyString([merged['started_at'], merged['startedAt']]);
    const finishedAt = this.firstNonEmptyString([
      merged['finished_at'],
      merged['finishedAt'],
      merged['completed_at'],
      merged['completedAt'],
    ]);

    const noteId = this.firstNonEmptyString([
      merged['note_id'],
      merged['noteId'],
      merged['document_id'],
      merged['doc_id'],
    ]);
    const noteTitle = this.firstNonEmptyString([merged['note_title'], merged['title'], merged['noteTitle']]);
    const normalizedJobType = this.normalizeJobType(jobTypeRaw, noteId, noteTitle, sourceLabel);

    const entry: ReindexHistoryEntry = {
      job_id: this.firstNonEmptyString([merged['job_id'], merged['jobId']]) || undefined,
      job_type: normalizedJobType,
      submitted_at: submittedAt || new Date().toISOString(),
      started_at: startedAt || undefined,
      finished_at: finishedAt || undefined,
      status: status || 'unknown',
      note_id: noteId || undefined,
      note_title: noteTitle || undefined,
      total_docs: this.toFiniteNumber(merged['total_docs']) ?? this.toFiniteNumber(merged['total']),
      total_docs_in_database:
        this.toFiniteNumber(merged['total_docs_in_database']) ?? this.toFiniteNumber(merged['total_docs']),
      processed_count:
        this.toFiniteNumber(merged['processed_count']) ?? this.toFiniteNumber(merged['docs_processed']),
      indexed_count: this.toFiniteNumber(merged['indexed_count']) ?? this.toFiniteNumber(merged['indexed']),
      skipped_count: this.toFiniteNumber(merged['skipped_count']) ?? this.toFiniteNumber(merged['skipped']),
      chunks_indexed:
        this.toFiniteNumber(merged['chunks_indexed']) ??
        this.toFiniteNumber(merged['chunk_count']) ??
        this.toFiniteNumber(merged['chunks']),
      error_count: this.toFiniteNumber(merged['error_count']) ?? this.toFiniteNumber(merged['errors']),
      error: this.firstNonEmptyString([merged['error'], merged['message']]) || undefined,
    };

    return entry;
  }

  private normalizeJobType(raw: string, noteId = '', noteTitle = '', sourceLabel = ''): 'bulk' | 'note' {
    const value = String(raw || '').toLowerCase();
    const source = String(sourceLabel || '').toLowerCase();
    if (noteId || noteTitle || source.includes('note') || source.includes('single')) {
      return 'note';
    }
    if (
      value.includes('note') ||
      value.includes('single') ||
      value === 'item' ||
      value === 'document'
    ) {
      return 'note';
    }
    return 'bulk';
  }

  private buildHistoryFromLocalActivity(limit: number): ReindexHistoryEntry[] {
    return getRecentAiIndexActivity(limit).map((entry): ReindexHistoryEntry => ({
      job_id: entry.jobId,
      job_type: entry.kind === 'note_reindex' ? 'note' : 'bulk',
      submitted_at: entry.timestamp,
      started_at: entry.timestamp,
      finished_at: entry.timestamp,
      status: entry.status,
      note_id: entry.noteId,
      note_title: entry.noteTitle,
      processed_count: entry.processed,
      indexed_count: entry.indexed,
      skipped_count: entry.skipped,
      chunks_indexed: entry.chunks,
      error: entry.error,
    }));
  }

  private firstNonEmptyString(values: unknown[]): string {
    for (const value of values) {
      const text = String(value ?? '').trim();
      if (text) {
        return text;
      }
    }
    return '';
  }

  private toFiniteNumber(value: unknown): number | undefined {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? Math.floor(numeric) : undefined;
  }

  refreshActiveReindexStatus(showFeedback = true): void {
    const jobId = String(this.activeReindexJobId ?? '').trim();
    if (!jobId) {
      if (showFeedback) {
        this.setMessage('No active reindex job ID to refresh.');
      }
      return;
    }

    this.service.getReindexJob(jobId).subscribe({
      next: (res) => {
        this.handleReindexJobUpdate(jobId, res?.data ?? null);
        if (showFeedback) {
          this.setMessage('Reindex job status refreshed.');
        }
      },
      error: (err) => {
        const statusCode = Number((err as any)?.status ?? 0);
        if (statusCode === 404) {
          this.markJobNoLongerTracked();
          if (showFeedback) {
            this.setMessage('Job no longer tracked (server restarted or job pruned).');
          }
          return;
        }

        if (showFeedback) {
          this.setError(this.extractError(err, 'Failed to refresh reindex job status.'));
        }
      },
    });
  }

  refreshDiagnostics(showSpinner = false): void {
    if (showSpinner && this.diagnosticsLoading) {
      return;
    }

    if (showSpinner) {
      this.diagnosticsLoading = true;
    }

    this.loading = true;
    this.clearMessages();
    this.refreshIndexHistory();

    this.service
      .getHealth()
      .pipe(finalize(() => {
        this.loading = false;
        if (showSpinner) {
          this.diagnosticsLoading = false;
        }
      }))
      .subscribe({
        next: (healthRes) => {
          this.healthData = healthRes?.data ?? null;
          this.loadIndexInfoAndStatus();
        },
        error: (err) => {
          this.healthData = null;
          this.indexInfo = null;
          this.reindexStatus = null;
          this.setError(this.extractError(err, 'Failed to load AI health.'));
        },
      });
  }

  onRefreshDiagnosticsClick(): void {
    this.refreshDiagnostics(true);
  }

  reindexNotes(): void {
    if (this.reindexing) {
      return;
    }

    const limit = this.normalizeLimit(this.reindexLimit);
    this.activeReindexRequestedLimit = limit;
    this.reindexing = true;
    this.clearMessages();
    this.stopReindexPolling();
    this.clearAllPendingJobs();

    const payload: { limit?: number; reset_collection: boolean } = { reset_collection: true };
    if (limit !== null) {
      payload.limit = limit;
    }

    this.service.reindexNotes(payload).subscribe({
      next: (res) => {
        const jobId = String(res?.data?.job_id ?? '').trim();
        if (!jobId) {
          this.reindexing = false;
          this.latestStartedReindexJobId = null;
          this.activeReindexJobId = null;
          this.activeReindexJobStatus = '';
          this.activeReindexSummary = '';
          this.setMessage('Reindex request accepted, but no job ID was returned.');
          this.refreshDiagnostics();
          return;
        }

        this.activeReindexJobId = jobId;
        this.latestStartedReindexJobId = jobId;
        this.activeReindexJobStatus = 'accepted';
        this.activeReindexSummary = 'Waiting for first progress update...';
        this.activeReindexLastUpdated = new Date().toISOString();
        this.activeReindexStartedAt = new Date().toISOString();
        this.activeReindexProgressPercent = null;
        this.recordFullReindexActivity(jobId, 'accepted', {
          details: this.activeReindexSummary,
          source: 'manual_start',
        });
        this.persistActiveReindexJob();
        this.refreshIndexHistory();
        this.setMessage(`Reindex job queued (${jobId}). Polling status...`);
        this.startReindexPolling(jobId);
      },
      error: (err) => {
        this.reindexing = false;
        this.latestStartedReindexJobId = null;
        this.recordFullReindexActivity('n/a', 'failed', {
          details: 'Failed to queue full reindex job.',
          error: this.extractError(err, 'Reindex failed.'),
          source: 'manual_start',
        });
        this.refreshIndexHistory();
        this.setError(this.extractError(err, 'Reindex failed.'));
      },
    });
  }

  clearIndex(): void {
    if (this.clearing) {
      return;
    }

    this.clearing = true;
    this.clearMessages();

    this.service
      .clearIndex()
      .pipe(finalize(() => (this.clearing = false)))
      .subscribe({
        next: () => {
          this.setMessage('Index cleared.');
          this.refreshDiagnostics();
        },
        error: (err) => this.setError(this.extractError(err, 'Clear index failed.')),
      });
  }

  createEmbeddingsForSingleNote(): void {
    const noteId = this.indexOperationNoteId.trim();
    if (!noteId || this.creatingSingleNoteIndex) {
      return;
    }

    this.creatingSingleNoteIndex = true;
    this.clearMessages();

    this.service
      .reindexSingleNote(noteId)
      .pipe(finalize(() => (this.creatingSingleNoteIndex = false)))
      .subscribe({
        next: (res) => {
          const data = (res?.data ?? {}) as Record<string, unknown>;
          const chunkCount = Number(data['new_chunk_count'] ?? 0);
          const chunkIds = Array.isArray(data['chunk_ids']) ? (data['chunk_ids'] as string[]) : [];

          appendAiIndexActivity({
            kind: 'note_reindex',
            timestamp: new Date().toISOString(),
            status: 'accepted',
            source: 'manual_single_note',
            noteId,
            details: `Queued manual note indexing (${chunkCount} chunk(s)).`,
            chunks: chunkCount,
          });
          this.refreshIndexHistory();

          this.pendingSingleNoteJobs.set(noteId, {
            status: 'queued',
            chunks: chunkCount,
            chunkIds: chunkIds,
            verified: false,
            attempts: 0,
            maxAttempts: this.singleNoteVerifyMaxAttempts,
            submittedAt: new Date(),
          });

          this.setMessage(
            `Reindex request accepted for ${noteId}. Built ${chunkCount} chunk(s); verifying embedding availability...`,
          );
          this.scheduleAutoVerify(noteId, 1);
        },
        error: (err) => {
          appendAiIndexActivity({
            kind: 'note_reindex',
            timestamp: new Date().toISOString(),
            status: 'failed',
            source: 'manual_single_note',
            noteId,
            error: this.extractError(err, 'Create embeddings request failed.'),
          });
          this.refreshIndexHistory();
          this.setError(this.extractError(err, 'Create embeddings request failed.'));
        },
      });
  }

  private scheduleAutoVerify(noteId: string, attempt: number): void {
    const existingTimer = this.singleNoteVerifyTimers.get(noteId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      this.verifySingleNoteEmbeddings(noteId, attempt);
      this.singleNoteVerifyTimers.delete(noteId);
    }, this.singleNoteVerifyIntervalMs);

    this.singleNoteVerifyTimers.set(noteId, timer);
  }

  verifySingleNoteEmbeddings(noteId: string, attempt = 1): void {
    const job = this.pendingSingleNoteJobs.get(noteId);
    if (!job) {
      return;
    }

    job.attempts = attempt;
    job.status = 'verifying';
    job.lastCheckedAt = new Date();

    this.service.getChromaNoteDebug(noteId).subscribe({
      next: (res) => {
        const payload = (res?.data ?? {}) as Record<string, unknown>;
        const exists = payload['exists'];

        job.verified = true;
        job.verifiedAt = new Date();

        if (exists === false) {
          if (attempt < this.singleNoteVerifyMaxAttempts) {
            job.status = 'pending_backend';
            job.verified = false;
            this.scheduleAutoVerify(noteId, attempt + 1);
            this.setMessage(
              `Embeddings for ${noteId} are still pending (${attempt}/${this.singleNoteVerifyMaxAttempts}). Rechecking...`,
            );
            return;
          }

          job.status = 'backend_timeout';
          this.setError(
            `Embeddings still not found for ${noteId} after ${this.singleNoteVerifyMaxAttempts} checks (~90s). ` +
              'Backend single-note indexing may not be persisting embeddings yet.',
          );
        } else {
          job.status = 'indexed';
          appendAiIndexActivity({
            kind: 'note_reindex',
            timestamp: new Date().toISOString(),
            status: 'success',
            source: 'manual_verify',
            noteId,
            details: 'Embeddings verified in index.',
          });
          this.refreshIndexHistory();
          this.setMessage(`Embeddings verified for ${noteId}.`);
        }
      },
      error: (err) => {
        const statusCode = Number((err as any)?.status ?? 0);
        if ((statusCode === 503 || statusCode === 504) && attempt < this.singleNoteVerifyMaxAttempts) {
          job.status = 'pending_backend';
          job.verified = false;
          this.scheduleAutoVerify(noteId, attempt + 1);
          this.setMessage(
            `AI indexing backend is temporarily unavailable (${statusCode}) for ${noteId} ` +
              `(${attempt}/${this.singleNoteVerifyMaxAttempts}). Rechecking...`,
          );
          return;
        }

        job.status = 'verify_failed';
        job.verified = true;
        job.verifiedAt = new Date();
        this.setError(this.extractError(err, `Failed to verify embeddings for ${noteId}.`));
      },
    });
  }

  deleteEmbeddingsForSingleNote(): void {
    const noteId = this.indexOperationNoteId.trim();
    if (!noteId || this.deletingSingleNoteIndex) {
      return;
    }

    this.deletingSingleNoteIndex = true;
    this.clearMessages();

    this.service
      .deleteSingleNoteFromIndex(noteId)
      .pipe(finalize(() => (this.deletingSingleNoteIndex = false)))
      .subscribe({
        next: () => {
          this.setMessage('Delete embeddings request completed for selected note.');
          this.refreshDiagnostics();
        },
        error: (err) => this.setError(this.extractError(err, 'Delete embeddings request failed.')),
      });
  }

  goHome(): void {
    this.router.navigate(['/home']);
  }

  goIndexManagement(): void {
    this.activeTab = 'maintenance';
  }

  setTab(tab: 'overview' | 'maintenance' | 'debug'): void {
    this.activeTab = tab;
  }


  private loadIndexInfoAndStatus(): void {
    this.service.getIndexInfo().subscribe({
      next: (indexRes) => {
        this.indexInfo = indexRes?.data ?? null;
        this.service.getReindexStatus().subscribe({
          next: (statusRes) => {
            this.reindexStatus = statusRes?.data ?? null;
          },
          error: () => {
            this.reindexStatus = null;
          },
        });
      },
      error: (err) => {
        this.indexInfo = null;
        this.reindexStatus = null;
        this.setError(this.extractError(err, 'Failed to load index info.'));
      },
    });
  }

  private normalizeLimit(value: number | null): number | null {
    if (value == null) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
  }

  private startReindexPolling(jobId: string): void {
    this.stopReindexPolling();
    this.reindexPollDelayMs = this.reindexPollInitialMs;
    this.pollReindexJob(jobId);
  }

  private handleReindexJobUpdate(jobId: string, data: AiReindexJobData | null): void {
    const status = String(data?.job_status ?? '').trim().toLowerCase();
    if (!status) {
      return;
    }

    this.activeReindexJobId = jobId;
    this.activeReindexJobStatus = status;
    this.activeReindexLastUpdated = new Date().toISOString();
    const startedAt = String((data as any)?.started_at ?? '').trim();
    if (startedAt) {
      this.activeReindexStartedAt = startedAt;
    }

    this.totalDocsInDatabase = Number(data?.total_docs_in_database ?? 0) || 0;
    this.docsProcessed = Number(data?.docs_processed ?? 0) || 0;
    this.chunksIndexed = Number(data?.chunks_indexed ?? 0) || 0;
    this.lastActivityAt = String(data?.last_activity_at ?? '').trim() || null;
    this.isJobStalled = this.detectStalledJob();

    const liveSummary = this.buildReindexLiveSummary(data);
    this.activeReindexSummary = liveSummary || '';
    this.activeReindexProgressPercent = this.computeReindexProgressPercent();
    this.recordFullReindexActivity(jobId, status, {
      details: liveSummary,
      processed: this.docsProcessed,
      chunks: this.chunksIndexed,
      source: 'poll',
      skipIfUnchangedStatus: true,
    });
    this.persistActiveReindexJob();

    if (!this.terminalReindexStates.has(status)) {
      const stalledWarning = this.isJobStalled ? ' [WARNING: No activity for 30+ seconds]' : '';
      this.setMessage(`Reindex job ${jobId} is ${status}.${stalledWarning}`);
      this.refreshIndexHistory();
      return;
    }

    this.reindexing = false;
    this.stopReindexPolling();
    this.latestStartedReindexJobId = null;

    if (status === 'failed') {
      const failure = typeof data?.error === 'string' && data.error.trim() ? data.error : 'Reindex job failed.';
      this.setError(failure);
      this.refreshDiagnostics();
      return;
    }

    const summary = this.buildReindexResultSummary(data?.result ?? {}) || liveSummary;
    const suffix = summary ? ` ${summary}` : '';
    this.activeReindexSummary = summary;
    this.recordFullReindexActivity(jobId, status, {
      details: summary,
      processed: this.docsProcessed,
      chunks: this.chunksIndexed,
      source: 'poll_terminal',
      error: status === 'completed_with_errors' ? 'Completed with errors.' : undefined,
      skipIfUnchangedStatus: false,
    });
    this.refreshIndexHistory();
    if (status === 'completed_with_errors') {
      this.setError(`Reindex completed with errors.${suffix}`);
    } else {
      this.setMessage(`Reindex completed.${suffix}`);
    }
    this.refreshDiagnostics();
  }

  private buildReindexResultSummary(result: Record<string, unknown>): string {
    const counts = [
      this.toCountLabel(result, ['indexed_count', 'indexed'], 'indexed'),
      this.toCountLabel(result, ['processed_count', 'processed'], 'processed'),
      this.toCountLabel(result, ['skipped_count', 'skipped'], 'skipped'),
      this.toCountLabel(result, ['chunk_reindex', 'chunks_reindexed'], 'chunks'),
    ].filter((v) => !!v);

    return counts.length ? counts.join(', ') : '';
  }

  private buildReindexLiveSummary(data: AiReindexJobData | null): string {
    if (!data) {
      return '';
    }

    const counts = [];

    if (this.totalDocsInDatabase > 0 && this.docsProcessed >= 0) {
      const percent = Math.round((this.docsProcessed / this.totalDocsInDatabase) * 100);
      counts.push(`processed: ${this.docsProcessed} / ${this.totalDocsInDatabase} (${percent}%)`);
    } else if (this.docsProcessed > 0) {
      counts.push(`processed: ${this.docsProcessed}`);
    }

    const result = this.extractReindexCounters(data);
    if (this.docsProcessed > 0) {
      const indexed = Number((data as any)?.indexed_count ?? result['indexed_count'] ?? 0) || 0;
      if (indexed > 0) {
        counts.push(`indexed: ${indexed}`);
      }
    }

    if (this.chunksIndexed > 0) {
      counts.push(`chunks: ${this.chunksIndexed}`);
    }

    const skipped = this.toNonZeroCountLabel(result, ['skipped_count', 'skipped'], '');
    if (skipped) {
      counts.push(`${skipped}`);
    }

    return counts.join(', ');
  }

  private computeReindexProgressPercent(): number | null {
    if (this.totalDocsInDatabase <= 0 || this.docsProcessed < 0) {
      return null;
    }

    const isTerminal = this.isTerminalReindexStatus(this.activeReindexJobStatus);
    if (isTerminal) {
      return 100;
    }

    const notesRatio = Math.min(1, this.docsProcessed / this.totalDocsInDatabase);

    if (notesRatio < 1) {
      return Math.round(notesRatio * 70);
    }

    const chunksPerNote = this.chunksIndexed / this.totalDocsInDatabase;
    const chunksPhaseProgress = 1 - 1 / (1 + chunksPerNote);
    return Math.min(99, Math.round(70 + 29 * chunksPhaseProgress));
  }

  isReindexFinalizing(): boolean {
    if (this.totalDocsInDatabase <= 0) {
      return false;
    }

    return (
      this.docsProcessed >= this.totalDocsInDatabase &&
      !this.isTerminalReindexStatus(this.activeReindexJobStatus)
    );
  }

  private isTerminalReindexStatus(status: string | null | undefined): boolean {
    const normalized = String(status ?? '').trim().toLowerCase();
    return this.terminalReindexStates.has(normalized);
  }

  private detectStalledJob(): boolean {
    if (!this.lastActivityAt || this.terminalReindexStates.has(this.activeReindexJobStatus)) {
      return false;
    }

    const lastActivity = new Date(this.lastActivityAt).getTime();
    const now = Date.now();
    const stalledThresholdMs = 30_000;

    return now - lastActivity > stalledThresholdMs;
  }

  private extractReindexCounters(data: AiReindexJobData): Record<string, unknown> {
    const nested = data?.result && typeof data.result === 'object' ? (data.result as Record<string, unknown>) : {};
    return {
      ...data,
      ...nested,
    };
  }

  private toCountLabel(source: Record<string, unknown>, keys: string[], label: string): string {
    for (const key of keys) {
      const value = source[key];
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        return `${label}: ${Math.floor(numeric)}`;
      }
    }
    return '';
  }

  private toNonZeroCountLabel(source: Record<string, unknown>, keys: string[], label: string): string {
    for (const key of keys) {
      const value = source[key];
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric > 0) {
        return `${label}: ${Math.floor(numeric)}`;
      }
    }
    return '';
  }

  private pollReindexJob(jobId: string): void {
    if (this.latestStartedReindexJobId !== jobId) {
      return;
    }

    this.reindexPollSub?.unsubscribe();
    this.reindexPollSub = this.service.getReindexJob(jobId).subscribe({
      next: (res) => {
        if (this.latestStartedReindexJobId !== jobId) {
          return;
        }

        const data = res?.data ?? null;
        this.handleReindexJobUpdate(jobId, data);
        const status = String(data?.job_status ?? '').trim().toLowerCase();
        if (!this.terminalReindexStates.has(status)) {
          this.scheduleNextReindexPoll(jobId);
        }
      },
      error: (err) => {
        if (this.latestStartedReindexJobId !== jobId) {
          return;
        }

        const statusCode = Number((err as any)?.status ?? 0);
        if (statusCode === 404) {
          this.markJobNoLongerTracked();
          this.setMessage('Job no longer tracked (server restarted or job pruned).');
          return;
        }

        this.setError(this.extractError(err, 'Failed while polling reindex job status.'));
        this.scheduleNextReindexPoll(jobId);
      },
    });
  }

  private scheduleNextReindexPoll(jobId: string): void {
    const delay = this.reindexPollDelayMs;
    this.reindexPollDelayMs = Math.min(this.reindexPollMaxMs, this.reindexPollDelayMs * 2);
    this.reindexPollTimer = setTimeout(() => this.pollReindexJob(jobId), delay);
  }

  private markJobNoLongerTracked(): void {
    this.reindexing = false;
    this.stopReindexPolling();
    this.latestStartedReindexJobId = null;
    this.activeReindexJobId = null;
    this.activeReindexJobStatus = 'not_tracked';
    this.activeReindexSummary = 'Job no longer tracked (server restarted or job pruned).';
    this.activeReindexLastUpdated = new Date().toISOString();
    this.activeReindexProgressPercent = null;
    this.persistActiveReindexJob();
  }

  private stopReindexPolling(): void {
    this.reindexPollSub?.unsubscribe();
    this.reindexPollSub = undefined;
    if (this.reindexPollTimer) {
      clearTimeout(this.reindexPollTimer);
      this.reindexPollTimer = undefined;
    }
  }

  private recordFullReindexActivity(
    jobId: string,
    status: string,
    options?: {
      details?: string;
      error?: string;
      source?: string;
      processed?: number;
      indexed?: number;
      skipped?: number;
      chunks?: number;
      skipIfUnchangedStatus?: boolean;
    },
  ): void {
    const normalizedJobId = String(jobId || '').trim();
    const normalizedStatus = String(status || '').trim().toLowerCase() || 'unknown';
    const shouldSkipIfUnchanged = options?.skipIfUnchangedStatus !== false;

    if (normalizedJobId && shouldSkipIfUnchanged) {
      const previous = this.lastLoggedReindexStatusByJob.get(normalizedJobId);
      if (previous === normalizedStatus) {
        return;
      }
      this.lastLoggedReindexStatusByJob.set(normalizedJobId, normalizedStatus);
    }

    appendAiIndexActivity({
      kind: 'full_reindex',
      timestamp: new Date().toISOString(),
      status: normalizedStatus,
      source: options?.source,
      jobId: normalizedJobId || undefined,
      details: options?.details,
      error: options?.error,
      processed: options?.processed,
      indexed: options?.indexed,
      skipped: options?.skipped,
      chunks: options?.chunks,
    });
  }

  private extractError(err: unknown, fallback: string): string {
    const e = err as { status?: unknown; error?: { error?: unknown; message?: unknown }; message?: unknown };
    const statusCode = Number(e?.status ?? 0);
    if (statusCode === 503 || statusCode === 504) {
      return 'AI backend is temporarily unavailable (service timeout/unavailable). Please retry in a few moments.';
    }

    const apiError = e?.error?.error;
    if (typeof apiError === 'string' && apiError.trim()) {
      return apiError;
    }

    const apiMessage = e?.error?.message;
    if (typeof apiMessage === 'string' && apiMessage.trim()) {
      return apiMessage;
    }

    const message = e?.message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }

    return fallback;
  }

  private setMessage(message: string): void {
    this.message = message;
    this.error = '';
  }

  private setError(message: string): void {
    this.error = message;
    this.message = '';
  }

  private clearMessages(): void {
    this.message = '';
    this.error = '';
  }

  getPendingJobsArray(): Array<{ noteId: string; job: any }> {
    return Array.from(this.pendingSingleNoteJobs.entries()).map(([noteId, job]) => ({
      noteId,
      job,
    }));
  }

  clearPendingJob(noteId: string): void {
    this.pendingSingleNoteJobs.delete(noteId);
    const timer = this.singleNoteVerifyTimers.get(noteId);
    if (timer) {
      clearTimeout(timer);
      this.singleNoteVerifyTimers.delete(noteId);
    }
  }

  private clearAllPendingJobs(): void {
    this.pendingSingleNoteJobs.clear();
    this.clearSingleNoteTimers();
  }

  private persistActiveReindexJob(): void {
    this.clearPersistedReindexJob();
  }

  private clearPersistedReindexJob(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.removeItem(ACTIVE_REINDEX_JOB_STORAGE_KEY);
  }
}
